import type { CrmNoteType } from '@plato/data/prisma';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { getTextContent } from '../../../lib/rich-text/get-text-content.ts';
import type { CrmNoteInput } from '../../../server/dto/crm-note-input-dto.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { RichTextInput } from '../../rich-text/components/rich-text-input.tsx';
import type { AIActionType } from '../../rich-text/hooks/use-improve-writing.ts';

export type NoteFormFieldsProps<T extends CrmNoteInput> = {
    noteType: CrmNoteType;
    control: Control<T>;
    errors: FieldErrors<T>;
    onAIInteraction?: (action: AIActionType, sessionId: string) => void;
    noteId?: string;
    placeholderOverride?: string;
};

export const NoteFormFields = <T extends CrmNoteInput>({
    noteType,
    control,
    errors,
    onAIInteraction,
    noteId,
    placeholderOverride,
}: NoteFormFieldsProps<T>) => {
    const { t } = useLocale();

    const getPlaceholderByType = (noteType: CrmNoteType) => {
        switch (noteType) {
            case 'VISIT':
                return t('notes__placeholder__event', { ns: 'notes' });
            case 'PHONE_CALL':
                return t('notes__placeholder__phone_call', { ns: 'notes' });
            default:
                return t('notes__placeholder__note', { ns: 'notes' });
        }
    };

    return (
        <DialogForm.ItemList>
            {/* Title/Description Field */}
            <DialogForm.Item>
                <DialogForm.Body className="box-border py-0">
                    <Controller
                        name="title"
                        control={control as Control<CrmNoteInput>}
                        rules={{
                            validate: (value) => {
                                if (noteType !== 'NOTE') {
                                    return true;
                                }

                                const textContent = getTextContent(value);
                                return !!textContent || t('form__text_required', { ns: 'common' });
                            },
                        }}
                        render={({ field }) => (
                            <RichTextInput
                                entityType="note"
                                entityId={noteId}
                                placeholder={placeholderOverride ?? getPlaceholderByType(noteType)}
                                onAIInteraction={onAIInteraction}
                                error={!!errors.title}
                                {...field}
                            />
                        )}
                    />
                </DialogForm.Body>
                <DialogForm.Error error={errors.title} />
            </DialogForm.Item>
        </DialogForm.ItemList>
    );
};
